import { redirectRoute } from '@/router/helpers';
import { useUserStore } from '@/stores/user';
import unleash from '@/unleash';

const Customers = () => import('@/modules/customers/index.vue');
const CustomersAll = () => import('@/modules/customers/all/index.vue');
const CustomersNew = () => import('@/modules/customers/new/index.vue');
const Customer = () => import('@/modules/customers/customer/index.vue');
const CustomerOverview = () =>
  import('@/modules/customers/customer/overview/index.vue');
const CustomerSales = () =>
  import('@/modules/customers/customer/sales/index.vue');
const CustomerForms = () =>
  import('@/modules/customers/customer/forms/index.vue');
const CustomerHistory = () =>
  import('@/modules/customers/customer/history/index.vue');
const CustomerReports = () =>
  import('@/modules/customers/customer/reports/index.vue');
const CustomerLoyaltyPoints = () =>
  import('@/modules/customers/customer/loyalty-points/index.vue');
const CustomerAppointments = () =>
  import('@/modules/customers/customer/appointments/index.vue');
const CustomerPrepaidCards = () =>
  import('@/modules/customers/customer/prepaid-cards/index.vue');
const CustomerFeedback = () =>
  import('@/modules/customers/customer/feedback/index.vue');
const CustomerIPL = () => import('@/modules/customers/customer/ipl/index.vue');
const CustomersSegments = () =>
  import('@/modules/customers/segments/index.vue');

const CreateCustomerReport = () => import('@/modals/CreateCustomerReport.vue');
const AppointmentReport = () => import('@/modals/AppointmentReport.vue');
const Sale = () => import('@/modals/sale/index.vue');
const InvoiceEmail = () => import('@/modals/invoice-email/index.vue');
const PrepaidCard = () =>
  import('@/modules/register/prepaid-cards/PrepaidCard.vue');
const Form = () => import('@/modals/forms/Form.vue');
const FormPreview = () => import('@/modals/forms/FormPreview.vue');
const Forms = () => import('@/modals/forms/Forms.vue');

import appointmentRoutes from './appointment';

const CreateEvent = () => import('@/modules/calendar/create-event/index.vue');
const Appointment = () => import('@/modules/calendar/appointment/index.vue');

const Imports = () => import('@/modules/customers/Imports.vue');

export default {
  path: 'customers',
  name: 'customers',
  component: Customers,
  redirect: {
    name: 'customers-all',
  },
  meta: {
    featureFlag: 'module-customers',
    moduleName: 'customers',
    role: 'basic',
  },
  children: [
    {
      path: '',
      name: 'customers-all',
      component: CustomersAll,
      children: [
        {
          path: 'new',
          name: 'customers-new',
          component: CustomersNew,
          props: {
            parentRouteName: 'customers-all',
          },
        },
      ],
    },
    {
      path: 'segments/:key?',
      name: 'customers-segments',
      component: CustomersSegments,
      meta: {
        unleash: 'CustomerSegments',
      },
    },
    {
      path: 'import',
      name: 'customers-imports',
      component: Imports,
    },
    {
      path: ':customerId',
      name: 'customer',
      component: Customer,
      redirect: { name: 'customer-overview' },
      children: [
        {
          path: 'overview',
          name: 'customer-overview',
          component: CustomerOverview,
          children: [
            {
              path: 'edit',
              name: 'customers-edit',
              component: CustomersNew,
              props: {
                parentRouteName: 'customer',
              },
            },
          ],
        },
        {
          path: 'edit',
          redirect: { name: 'customers-edit' },
        },
        {
          path: 'history',
          name: 'customer-history',
          component: CustomerHistory,
        },
        {
          path: 'reports',
          name: 'customer-reports',
          component: CustomerReports,
          children: [
            {
              path: 'new',
              name: 'customer-report-create',
              component: CreateCustomerReport,
              props: {
                parentRouteName: 'customer-reports',
              },
            },
            {
              path: ':id',
              name: 'customer-report',
              component: AppointmentReport,
              props: {
                parentRouteName: 'customer-reports',
              },
            },
            {
              path: ':id/edit',
              name: 'customer-report-edit',
              component: CreateCustomerReport,
              props: {
                parentRouteName: 'customer-reports',
              },
            },
          ],
        },
        {
          path: 'loyalty-points',
          name: 'customer-loyalty-points',
          component: CustomerLoyaltyPoints,
        },
        {
          path: 'sales',
          name: 'customer-sales',
          component: CustomerSales,
          children: [
            {
              path: ':id',
              name: 'customer-sale',
              component: Sale,
              props: {
                parentRouteName: 'customer-sales',
              },
            },
            {
              path: ':id/email',
              name: 'customer-sale-email',
              component: InvoiceEmail,
              props: {
                parentRouteName: 'customer-sales',
              },
            },
          ],
        },
        {
          path: 'invoices',
          redirect: { name: 'customer-sales' },
        },
        {
          path: 'invoices/:id',
          redirect: (to) => ({ name: 'customer-sale', params: to.params }),
        },
        {
          path: 'invoices/:id/email',
          redirect: (to) => ({
            name: 'customer-sale-email',
            params: to.params,
          }),
        },
        {
          path: 'appointments',
          name: 'customer-appointments',
          component: CustomerAppointments,
          children: [
            {
              ...appointmentRoutes('customer-appointment'),
              path: ':appointmentId',
              name: 'customer-appointment',
              props: {
                parentRouteName: 'customer-appointments',
              },
            },
            {
              path: ':appointmentId/edit',
              component: CreateEvent,
              redirect: {
                name: 'customer-edit-appointment',
              },
              children: [
                {
                  path: '',
                  name: 'customer-edit-appointment',
                  component: Appointment,
                },
              ],
            },
          ],
        },
        {
          path: 'prepaid-cards',
          name: 'customer-prepaid-cards',
          component: CustomerPrepaidCards,
          beforeEnter: (to) => {
            const { hasFeatureFlag } = useUserStore();
            if (
              unleash.isEnabled('EnableFiskalySign') &&
              hasFeatureFlag('fiskaly-sign')
            ) {
              return redirectRoute({
                name: 'customer-overview',
                params: to.params,
              });
            }
          },
          children: [
            {
              path: ':id',
              name: 'customer-prepaid-card',
              component: PrepaidCard,
              props: {
                parentRouteName: 'customer-prepaid-cards',
              },
            },
          ],
        },
        {
          path: 'forms',
          name: 'customer-forms',
          component: CustomerForms,
          children: [
            {
              path: 'new/:templateId',
              name: 'customer-form',
              component: Form,
              props: {
                parentRouteName: 'customer-forms',
              },
            },
            {
              path: ':subId/edit/:templateId',
              name: 'customer-form-edit',
              component: Form,
              props: {
                parentRouteName: 'customer-forms',
              },
            },
            {
              path: 'select',
              name: 'customer-forms-select',
              component: Forms,
              props: {
                parentRouteName: 'customer-forms',
              },
            },
            {
              path: ':id',
              name: 'customer-form-preview',
              component: FormPreview,
              props: {
                parentRouteName: 'customer-forms',
              },
            },
          ],
        },
        {
          path: 'feedback',
          name: 'customer-feedback',
          component: CustomerFeedback,
        },
        {
          path: 'ipl-treatments',
          name: 'customer-ipl',
          component: CustomerIPL,
        },
      ],
    },
  ],
};
